import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ButtonModule, TooltipModule } from '@coreui/angular';
import { IconDirective, IconModule } from '@coreui/icons-angular';
import { ColumnState, FilterModel, GridApi } from 'ag-grid-community';

@Component({
  selector: 'smarsh-aggrid-reset-filters',
  standalone: true,
  imports: [
    IconDirective,
    IconModule,
    ButtonModule,
    CommonModule,
    TooltipModule
  ],
  template: `
    <button
      *ngIf="showButton"
      cButton
      color="light"
      cTooltip="Reset Filters"
      cTooltipPlacement="left"
      (click)="resetFilters()">
      <svg cIcon name="cilFilterX"></svg>
    </button>
  `,
  styles: ''
})
export class AgGridResetFiltersButtonComponent implements OnInit, OnDestroy {
  @Input() gridApi!: GridApi;
  @Input() enableSortingReset: boolean = false;

  private initialFilterModel: FilterModel = {};
  private initialColumnState: ColumnState[] = [];
  showButton = false;
  filtersChanged = false;
  sortingChanged = false;

  ngOnInit(): void {
    if (!this.gridApi) return;
    this.gridApi.addEventListener(
      'firstDataRendered',
      this.onFirstDataRendered.bind(this)
    );
  }

  ngOnDestroy(): void {
    if (!this.gridApi) return;
    this.removeEventListeners();
  }

  private onFirstDataRendered(): void {
    this.captureInitialState();
    this.addEventListeners();
  }

  private addEventListeners(): void {
    this.gridApi.addEventListener('filterChanged', this.onStateChanged);
    if (this.enableSortingReset) {
      this.gridApi.addEventListener('sortChanged', this.onStateChanged);
    }
  }

  private removeEventListeners(): void {
    this.gridApi.removeEventListener('filterChanged', this.onStateChanged);
    this.gridApi.removeEventListener('sortChanged', this.onStateChanged);
    this.gridApi.removeEventListener(
      'firstDataRendered',
      this.onFirstDataRendered
    );
  }

  private captureInitialState(): void {
    this.initialFilterModel = this.gridApi.getFilterModel();
    this.initialColumnState = this.gridApi.getColumnState();
  }

  private onStateChanged = (): void => {
    const currentFilterModel = this.gridApi.getFilterModel();
    const currentColumnState = this.gridApi.getColumnState();

    this.filtersChanged = !this.areModelsEqual(
      currentFilterModel,
      this.initialFilterModel
    );
    this.sortingChanged =
      this.enableSortingReset &&
      !this.areModelsEqual(currentColumnState, this.initialColumnState);

    this.showButton = this.filtersChanged || this.sortingChanged;
  };

  private areModelsEqual(obj1, obj2): boolean {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
  }

  resetFilters(): void {
    if (!this.gridApi) return;

    this.restoreFilterModel();

    if (this.sortingChanged) {
      this.restoreSortingModel();
    }

    this.notifyGridOfChanges();
    this.showButton = false;
  }

  private restoreFilterModel(): void {
    this.gridApi.setFilterModel(this.initialFilterModel);
  }

  private restoreSortingModel(): void {
    this.gridApi.applyColumnState({
      state: this.initialColumnState,
      applyOrder: true
    });
  }

  private notifyGridOfChanges(): void {
    this.gridApi.onFilterChanged();
    if (this.sortingChanged) {
      this.gridApi.onSortChanged();
    }
  }

  resetState(): void {
    if (!this.gridApi) return;
    this.showButton = false;
    this.captureInitialState();
  }
}
